import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
	whatsappId: Yup.string().required("Required"),
});

const onlyDigits = (value = "") => String(value).replace(/\D/g, "");

// número completo já no padrão BR (55 + DDD + 8/9 dígitos) — só nesse caso
// é seguro remover o 55 pra editar com máscara sem truncar dado legado estranho
const isStandardBrNumber = digits => /^55\d{10,11}$/.test(digits);

const stripBrCountryCode = digits =>
	isStandardBrNumber(digits) ? digits.slice(2) : digits;

const formatBrPhone = raw => {
	const d = onlyDigits(raw).slice(0, 11);
	let out = "";
	if (d.length > 0) out += `(${d.slice(0, 2)}`;
	if (d.length >= 2) out += ") ";
	if (d.length > 2) out += d.slice(2, 3);
	if (d.length > 3) out += d.slice(3, 7);
	if (d.length > 7) out += `-${d.slice(7, 11)}`;
	return out;
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		whatsappId: "",
	};

	const [contact, setContact] = useState(initialState);
	const [whatsapps, setWhatsapps] = useState([]);
	const [useNumberMask, setUseNumberMask] = useState(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchWhatsapps = async () => {
			try {
				const { data } = await api.get("/whatsapp");
				setWhatsapps(data || []);
			} catch (err) {
				toastError(err);
			}
		};
		fetchWhatsapps();
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) {
				setUseNumberMask(true);
				return;
			}

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					const rawDigits = onlyDigits(data.number);
					if (isStandardBrNumber(rawDigits)) {
						setUseNumberMask(true);
						setContact({ ...data, number: stripBrCountryCode(rawDigits) });
					} else {
						// número fora do padrão (ex: LID/legado, ver item 45 do HISTORICO_AJUSTES.md)
						// — não mascara pra não truncar/corromper esse dado ao salvar
						setUseNumberMask(false);
						setContact(data);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		const payload = {
			...values,
			number: useNumberMask
				? `55${onlyDigits(values.number)}`
				: onlyDigits(values.number),
		};
		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, payload);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", payload);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue, handleBlur }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								{useNumberMask ? (
									<TextField
										label={i18n.t("contactModal.form.number")}
										name="number"
										value={formatBrPhone(values.number)}
										onChange={e => {
											setFieldValue(
												"number",
												onlyDigits(e.target.value).slice(0, 11)
											);
										}}
										onBlur={handleBlur}
										error={touched.number && Boolean(errors.number)}
										helperText={
											(touched.number && errors.number) ||
											"DDD + número (o 55 do Brasil é adicionado automaticamente)"
										}
										placeholder="(85) 98686-4048"
										variant="outlined"
										margin="dense"
									/>
								) : (
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.number")}
										name="number"
										error={touched.number && Boolean(errors.number)}
										helperText={touched.number && errors.number}
										placeholder="5513912344321"
										variant="outlined"
										margin="dense"
									/>
								)}
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<div>
									<FormControl
										variant="outlined"
										margin="dense"
										fullWidth
										error={touched.whatsappId && Boolean(errors.whatsappId)}
									>
										<InputLabel id="whatsapp-selection-input-label">
											Conexão
										</InputLabel>
										<Field
											as={Select}
											label="Conexão"
											name="whatsappId"
											labelId="whatsapp-selection-label"
											id="whatsapp-selection"
											required
										>
											{whatsapps.map(whatsapp => (
												<MenuItem key={whatsapp.id} value={whatsapp.id}>
													📱 {whatsapp.name}
												</MenuItem>
											))}
										</Field>
									</FormControl>
								</div>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
